import { Accordion as ChakraAccordion, AccordionItem } from "@chakra-ui/react";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { Text, TextBold } from "src/apps/components/typography/Texts";

import { MEDIA_MOBILE_BREAKPOINT } from "src/apps/config/constants";
import styled, { useTheme } from "styled-components";
import { IdentityVerificationProcessingState } from "./IdentityVerificationProcessingState";
import { useIdentityVerification } from "src/apps/hooks/identityVerification/useIdentityVerification";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { OrderStatus } from "src/contexts/documentationArea/domain/OrderStatus";
import { Button } from "src/apps/components/button/Button";
import { IdentityVerificationsContent } from "./IdentityVerificationsContent";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { IdentityVerificationManualUploadState } from "./IdentityVerificationManualUploadState";
import { LuArrowUpFromLine } from "react-icons/lu";
import { colors } from "@permisso/permisso-components";
import { TEST_IDS } from "src/apps/config/testIds";

const BANNER_TITLE = "documentation_area.identity_verification.manual_upload_banner.title";
const BANNER_SUBTITLE_DESKTOP =
  "documentation_area.identity_verification.manual_upload_banner.subtitle.desktop";
const BANNER_SUBTITLE_MOBILE =
  "documentation_area.identity_verification.manual_upload_banner.subtitle.mobile";
const SECTION_TITLE = "documentation_area.uploader_box.identity_verification";

interface IdentityVerificationsManualUploadSectionProps {
  isSelected: boolean;
  isReadonly: boolean;
  state: IdentityVerificationManualUploadState;
  onClick: () => void;
}

export const IdentityVerificationsManualUploadSection = ({
  isSelected,
  isReadonly,
  state,
  onClick,
}: IdentityVerificationsManualUploadSectionProps) => {
  const { isMobile } = useIsMedia();
  const { identityVerificationStatus } = useIdentityVerification();
  const { primaryColor, blackColor, successColor, errorColor } = useTheme();
  const { t } = useTranslations();

  if (identityVerificationStatus === OrderStatus.PROCESSING)
    return <IdentityVerificationProcessingState />;

  const borderRadius = isMobile ? "0.5rem" : "0.938rem";

  const computeBorderColor = () => {
    if (isSelected) {
      return primaryColor;
    }

    return state === IdentityVerificationManualUploadState.COMPLETED
      ? successColor
      : colors.grey400;
  };

  const computeIconBasedOnResponses = (state: IdentityVerificationManualUploadState) => {
    return (
      <>
        {state === IdentityVerificationManualUploadState.COMPLETED ? (
          <Icon path={ICON_PATHS.CHECK_CIRCLE_FILLED} size={ICON_SIZES.s20} color={successColor} />
        ) : state === IdentityVerificationManualUploadState.ERROR ? (
          <Icon path={ICON_PATHS.CROSS_CIRCLE_THIN} size={ICON_SIZES.s20} color={errorColor} />
        ) : null}
      </>
    );
  };

  const computeHeader = () => {
    return isSelected && isMobile ? (
      <MobileContainer>
        {computeIconBasedOnResponses(state)}
        <TextBold $color={colors.grey700}>{t(SECTION_TITLE)}</TextBold>
      </MobileContainer>
    ) : (
      <Container>
        <Shape>
          <Icon
            path={ICON_PATHS.ARROW_UP_FROM_BRACKET}
            size={ICON_SIZES.s24}
            color={primaryColor}
          />
        </Shape>
        <TextContainer>
          <TextBold $color={blackColor}>{t(BANNER_TITLE)}</TextBold>
          <SecondLine>
            <Text $color={colors.grey700}>
              {isMobile ? t(BANNER_SUBTITLE_MOBILE) : t(BANNER_SUBTITLE_DESKTOP)}
            </Text>
          </SecondLine>
        </TextContainer>
      </Container>
    );
  };

  return (
    <StyledChakraAccordion
      $borderColor={computeBorderColor()}
      $borderRadius={borderRadius}
      allowMultiple
      index={isSelected ? [0] : []}
    >
      <StyledAccordionItem>
        <HeaderContainer>
          {computeHeader()}
          {!isReadonly ? (
            <ButtonContainer>
              {!isSelected ? (
                <Button
                  variant="outline"
                  text={t("common.upload_file")}
                  onClick={onClick}
                  dataTestId={TEST_IDS.identityVerification.manualUploadButton}
                  leftIcon={<LuArrowUpFromLine size={20} />}
                />
              ) : null}
            </ButtonContainer>
          ) : null}
        </HeaderContainer>
        {isSelected ? <IdentityVerificationsContent /> : null}
      </StyledAccordionItem>
    </StyledChakraAccordion>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  width: fit-content;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    width: 100%;
  }
`;

const MobileContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 1rem;
`;

const SecondLine = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: center;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
`;

const StyledChakraAccordion = styled(ChakraAccordion)<{
  $borderColor: string;
  $borderRadius: string;
}>`
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: ${({ $borderRadius }) => $borderRadius};
  padding: 2rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    padding: 1rem;
  }
`;

const StyledAccordionItem = styled(AccordionItem)`
  border-top: none;
  border-bottom: none;
  padding: 0 !important;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex: 1;
  text-align: left;
  gap: 1.5rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    flex-direction: column;
  }
`;

const Shape = styled.div`
  display: flex;
  width: 3.125rem;
  height: 3.125rem;
  align-items: center;
  justify-content: center;
  background-color: ${colors.grey0};
  border-radius: 0.5rem;
  filter: drop-shadow(0.927px 0.927px 4.644px rgba(0, 0, 0, 0.08));
`;
